/**
 * Build-time switches. Hard-coded on purpose, see below.
 */

/**
 * Whether card payments are open.
 *
 * `true` sends paid CTAs to server-created Checkout Sessions. `false` swaps
 * those controls for waitlist capture (`pricing:<tier>`) without removing the
 * Stripe integration — kill switch if Checkout or the Stripe account fails.
 *
 * **Prices stay visible either way.** Hiding the pricing page, tiers or legal
 * pages is never the right response to a Stripe outage; only the paid button
 * changes.
 *
 * Not an env var. An env var invites a deploy where the site says one thing and
 * the Worker does another. The server has its own independent guard:
 * `billing/checkout.ts` answers 503 when `STRIPE_SECRET_KEY` or the price ids
 * are unbound, so a client that ignores this flag still cannot start a
 * checkout without those bindings.
 */
// Annotated `boolean` rather than left to infer a literal. Without it every
// `BILLING_LIVE ?` branch narrows and the dead arm is reported unreachable /
// dropped by the bundler — which is how the kill-switch path would quietly
// break.
export const BILLING_LIVE: boolean = false;

/**
 * 14-day free trial on first paid Checkout for signed-in free accounts that
 * have never trialled. Must match `FEATURES.paidPlanTrial` in apps/api — flip
 * both together. When false, never claim a trial in UI or legal copy.
 *
 * Annotated `boolean` for the same dead-arm reason as `BILLING_LIVE`.
 */
export const PAID_PLAN_TRIAL: boolean = false;

/** Length shown in marketing/legal when `PAID_PLAN_TRIAL` is true. */
export const PAID_PLAN_TRIAL_DAYS = 14;

/**
 * Shown wherever a payment control would otherwise be when `BILLING_LIVE` is
 * false. Kill-switch copy only; not rendered while payments are open.
 */
export const BILLING_PENDING_NOTE =
    'Card payments are being activated. The plans and prices below are final. Join the list for a tier and we will email you the day it opens. The free tier is live now.';

/**
 * The same fact, worded for the legal pages while payments are closed.
 *
 * Those pages describe cancelling through the Stripe customer portal. When
 * paid CTAs are hidden, the note stops a reader looking for a control that is
 * not rendered.
 */
export const BILLING_PENDING_LEGAL_NOTE =
    'Paid plans are not open yet. No card can be charged and no subscription exists, so nothing on this page can cost you anything today. It describes how billing works from the day paid plans open.';
